import React, {useEffect, useMemo, useState} from 'react';
import {useTranslation} from 'react-i18next';
import AmountHighlight from '@/components/business/amount-highlight';
import {toPriceStr} from '@/utils';
import {
  AgentReportFilter,
  getAgentReportRange,
  useAgentDataStore,
} from './agent-data.store';
import AgentTabLoading from './agent-tab-loading';
import {AgentInfo} from '../agent.type';
import {AgentStatusCard} from './invitation-content';
import MyDataFilterTabs from './my-data-filter-tabs';

type MyDataMetricConfig = {
  labelKey: string;
  value: string;
  accent?: boolean;
};

type TeamDataMetricConfig = MyDataMetricConfig & {
  targetTab: MyDataDashboardTargetTab;
};

type MyDataDashboardConfig = {
  titleKey: string;
  targetTab: MyDataDashboardTargetTab;
  items: MyDataMetricConfig[];
};

type MyDataDashboardTargetTab =
  | 'invitation'
  | 'deposit-bonus'
  | 'subordinate-data'
  | 'commission';

type MyDataContentProps = {
  login: boolean;
  agentInfo?: AgentInfo;
  onTabChange: (tab: MyDataDashboardTargetTab) => void;
};

const parseAmount = (value?: string | number) => Number(value || 0) || 0;

const formatAmount = (value?: string | number) =>
  toPriceStr(parseAmount(value), {thousands: true, fixed: 2});

function MetricCard({
  label,
  value,
  onPress,
  accent,
}: {
  label: string;
  value: string;
  onPress: () => void;
  accent?: boolean;
}) {
  return (
    <button
      type="button"
      onClick={onPress}
      className="relative h-[3.9485rem] cursor-pointer rounded-[0.625rem] border-[0.061125rem] border-white/10 bg-white px-3 [touch-action:manipulation] active:scale-[0.99] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-[-0.125rem] focus-visible:outline-[#FB6B0B]/40">
      <div className="absolute inset-x-3 top-0 flex h-[1.9375rem] items-center justify-center overflow-hidden text-center text-[0.75rem] font-normal leading-[0.9375rem] tracking-[-0.01833125rem] text-[#564715]">
        {label}
      </div>
      <div className="absolute left-3 right-3 top-[1.9375rem] h-[0.0625rem] bg-black/10" />
      <div
        className={[
          'absolute inset-x-3 bottom-0 top-[2rem] flex items-center justify-center truncate text-center text-[0.75rem] font-bold leading-[0.9375rem] tracking-[-0.01833125rem]',
          accent ? '' : 'text-[#6E3A02]',
        ].join(' ')}>
        {accent ? (
          <AmountHighlight className="truncate">{value}</AmountHighlight>
        ) : (
          value
        )}
      </div>
    </button>
  );
}

function MetricCardSkeleton() {
  return (
    <div className="relative h-[3.9485rem] animate-pulse rounded-[0.625rem] border-[0.061125rem] border-white/10 bg-white px-3">
      <div className="absolute left-6 right-6 top-[0.8125rem] h-3 rounded-full bg-[#FFE8A1]/70" />
      <div className="absolute left-3 right-3 top-[1.9375rem] h-[0.0625rem] bg-black/10" />
      <div className="absolute left-8 right-8 top-[2.8125rem] h-3 rounded-full bg-[#FFE8A1]/70" />
    </div>
  );
}

function DashboardCard({
  title,
  items,
  onTitlePress,
  valueLarge,
}: {
  title: string;
  items: Array<{label: string; value: string; accent?: boolean}>;
  onTitlePress: () => void;
  valueLarge?: boolean;
}) {
  return (
    <article className="relative h-[5.9375rem] overflow-hidden rounded-[0.625rem] border-[0.5px] border-white/25 bg-white">
      <span
        aria-hidden="true"
        className="pointer-events-none absolute inset-x-0 bottom-0 top-[1.875rem] z-[1] rounded-b-[0.625rem] border-x-[0.5px] border-b-[0.5px] border-white/25"
      />
      <span
        aria-hidden="true"
        className="pointer-events-none absolute inset-x-0 top-0 z-[2] h-[1.875rem] rounded-t-[0.5rem] bg-[#FFEDB5] shadow-[inset_0_0.0625rem_0_rgba(121,251,255,0.07)]"
      />
      <button
        type="button"
        onClick={onTitlePress}
        className="absolute left-0 top-0 z-[3] h-[2.75rem] w-full cursor-pointer border-0 bg-transparent p-0 text-center text-[0.75rem] font-semibold leading-[0.9375rem] tracking-[-0.025em] text-[#564715] [touch-action:manipulation] transition-colors duration-150 active:text-black focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-[-0.125rem] focus-visible:outline-[#FB6B0B]/40">
        <span className="absolute left-8 right-8 top-[0.4375rem] truncate">
          {title}
        </span>
        <svg
          xmlns="http://www.w3.org/2000/svg"
          width="0.875rem"
          height="0.875rem"
          viewBox="0 0 14 14"
          fill="none"
          aria-hidden="true"
          className="absolute right-[0.625rem] top-2">
          <g opacity="0.9">
            <path
              d="M5 10.5L8.5 7L5 3.5"
              stroke="#564715"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </g>
        </svg>
      </button>
      <div className="absolute left-[1.125rem] right-[1.125rem] top-[2.75rem] grid h-[2.4375rem] grid-cols-3 items-start text-center">
        {items.map(item => (
          <div key={item.label} className="min-w-0 text-center">
            <div className="truncate text-[0.75rem] font-normal leading-[0.9375rem] tracking-[-0.025em] text-[#564715]">
              {item.label}
            </div>
            <div
              className={[
                'mt-[0.4375rem] truncate font-bold tracking-[-0.025em]',
                valueLarge
                  ? 'text-[0.875rem] leading-[1.0625rem]'
                  : 'text-[0.75rem] leading-[0.9375rem]',
                'text-[#6E3A02]',
              ].join(' ')}>
              {item.value}
            </div>
          </div>
        ))}
      </div>
    </article>
  );
}

function DashboardCardSkeleton() {
  return (
    <article className="relative h-[5.9375rem] animate-pulse overflow-hidden rounded-[0.625rem] border-[0.5px] border-white/25 bg-white">
      <span
        aria-hidden="true"
        className="pointer-events-none absolute inset-x-0 top-0 h-[1.875rem] rounded-t-[0.5rem] bg-[#FFEDB5]"
      />
      <div className="absolute left-20 right-20 top-[0.5625rem] h-3 rounded-full bg-[#FFE8A1]" />
      <div className="absolute left-[1.125rem] right-[1.125rem] top-[2.75rem] grid h-[2.4375rem] grid-cols-3 items-start gap-3">
        {Array.from({length: 3}).map((_, index) => (
          <div key={index} className="min-w-0">
            <div className="mx-auto h-3 w-4/5 rounded-full bg-[#FFE8A1]/70" />
            <div className="mx-auto mt-[0.4375rem] h-3 w-3/5 rounded-full bg-[#FFE8A1]/70" />
          </div>
        ))}
      </div>
    </article>
  );
}

const MyDataContent = ({login, agentInfo, onTabChange}: MyDataContentProps) => {
  const {t} = useTranslation();
  const [activeFilter, setActiveFilter] = useState<AgentReportFilter>('today');
  const levelOneTeamReport = useAgentDataStore(
    state => state.levelOneTeamReport,
  );
  const rewardBetSummary = useAgentDataStore(state => state.rewardBetSummary);
  const myDataLoading = useAgentDataStore(state => state.myDataLoading);
  const loadMyData = useAgentDataStore(state => state.loadMyData);
  const showSkeleton =
    myDataLoading && !levelOneTeamReport && !rewardBetSummary;

  const range = useMemo(
    () => getAgentReportRange(activeFilter),
    [activeFilter],
  );

  useEffect(() => {
    loadMyData(range);
  }, [loadMyData, range]);

  const teamCards: TeamDataMetricConfig[] = [
    {
      labelKey: 'members',
      value: `${levelOneTeamReport?.memberCount || 0}`,
      targetTab: 'subordinate-data',
    },
    {
      labelKey: 'firstDeposits',
      value: `${levelOneTeamReport?.firstRechargeUserCount || 0}`,
      targetTab: 'deposit-bonus',
    },
    {
      labelKey: 'deposits',
      value: `${levelOneTeamReport?.rechargeUserCount || 0}`,
      targetTab: 'deposit-bonus',
    },
    {
      labelKey: 'inviteBonus',
      value: formatAmount(levelOneTeamReport?.inviteReward),
      targetTab: 'invitation',
    },
    {
      labelKey: 'firstDepositAmount',
      value: formatAmount(levelOneTeamReport?.firstRechargeAmount),
      targetTab: 'deposit-bonus',
    },
    {
      labelKey: 'depositAmount',
      value: formatAmount(levelOneTeamReport?.rechargeAmount),
      targetTab: 'deposit-bonus',
    },
    {
      labelKey: 'depositBonus',
      value: formatAmount(levelOneTeamReport?.rechargeReward),
      targetTab: 'deposit-bonus',
    },
    {
      labelKey: 'betAmounts',
      value: formatAmount(levelOneTeamReport?.betAmount),
      targetTab: 'commission',
    },
    {
      labelKey: 'commission',
      value: formatAmount(levelOneTeamReport?.commissionAmount),
      targetTab: 'commission',
    },
  ];

  const dashboardGroups: MyDataDashboardConfig[] = [
    {
      titleKey: 'teamMembers',
      targetTab: 'subordinate-data',
      items: [
        {
          labelKey: 'allTeam',
          value: `${rewardBetSummary?.teamMemberCount || 0}`,
        },
        {
          labelKey: 'teamA',
          value: `${rewardBetSummary?.levelOneMemberCount || 0}`,
        },
        {
          labelKey: 'otherTeam',
          value: `${rewardBetSummary?.lowerLevelMemberCount || 0}`,
        },
      ],
    },
    {
      titleKey: 'bonus',
      targetTab: 'invitation',
      items: [
        {
          labelKey: 'allTeam',
          value: formatAmount(rewardBetSummary?.totalReward),
        },
        {
          labelKey: 'inviteBonus',
          value: formatAmount(rewardBetSummary?.inviteReward),
        },
        {
          labelKey: 'depositBonus',
          value: formatAmount(rewardBetSummary?.rechargeReward),
        },
      ],
    },
    {
      titleKey: 'betAmounts',
      targetTab: 'commission',
      items: [
        {
          labelKey: 'allTeam',
          value: formatAmount(rewardBetSummary?.totalBetAmount),
        },
        {
          labelKey: 'teamA',
          value: formatAmount(rewardBetSummary?.levelOneBetAmount),
        },
        {
          labelKey: 'otherTeam',
          value: formatAmount(rewardBetSummary?.lowerLevelBetAmount),
        },
      ],
    },
    {
      titleKey: 'betCommission',
      targetTab: 'commission',
      items: [
        {
          labelKey: 'allTeam',
          value: formatAmount(rewardBetSummary?.totalBetCommission),
          accent: true,
        },
        {
          labelKey: 'teamA',
          value: formatAmount(rewardBetSummary?.levelOneBetCommission),
          accent: true,
        },
        {
          labelKey: 'otherTeam',
          value: formatAmount(rewardBetSummary?.lowerLevelBetCommission),
          accent: true,
        },
      ],
    },
  ];

  return (
    <>
      <AgentStatusCard login={login} agentInfo={agentInfo} />
      <MyDataFilterTabs
        activeFilter={activeFilter}
        onFilterChange={setActiveFilter}
      />
      <section className="relative min-h-[18rem] px-0 pb-4 pt-[0.875rem]">
        <AgentTabLoading active={myDataLoading && !showSkeleton} />
        <h2 className="m-0 px-[0.5625rem] text-left text-[0.75rem] font-semibold leading-[0.9375rem] tracking-[-0.025em] text-black">
          {t('invitation.myData.teamAData')}
        </h2>
        <div className="mt-[0.6875rem] grid grid-cols-3 gap-x-[0.4375rem] gap-y-[0.5625rem]">
          {showSkeleton
            ? Array.from({length: 9}).map((_, index) => (
                <MetricCardSkeleton key={index} />
              ))
            : teamCards.map(card => (
                <MetricCard
                  key={card.labelKey}
                  label={t(`invitation.myData.metrics.${card.labelKey}`)}
                  value={card.value}
                  onPress={() => onTabChange(card.targetTab)}
                  accent={card.accent}
                />
              ))}
        </div>

        <section className="mt-4 rounded-[0.625rem] border border-white/10 bg-[#F4CFE3] px-[0.8125rem] pb-[0.875rem] pt-2">
          <h2 className="m-0 text-left text-[0.75rem] font-semibold leading-[0.9375rem] tracking-[-0.025em] text-black">
            {t('invitation.myData.dashboard')}
          </h2>
          <div className="mt-4 flex flex-col gap-[0.625rem]">
            {showSkeleton
              ? Array.from({length: 4}).map((_, index) => (
                  <DashboardCardSkeleton key={index} />
                ))
              : dashboardGroups.map(group => (
                  <DashboardCard
                    key={group.titleKey}
                    title={t(
                      `invitation.myData.dashboardCards.${group.titleKey}`,
                    )}
                    onTitlePress={() => onTabChange(group.targetTab)}
                    valueLarge={group.titleKey === 'teamMembers'}
                    items={group.items.map(item => ({
                      label: t(`invitation.myData.metrics.${item.labelKey}`),
                      value: item.value,
                      accent: item.accent,
                    }))}
                  />
                ))}
          </div>
        </section>
      </section>
    </>
  );
};

export default MyDataContent;
